/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import SegmentModel from 'cio/models/segment';
import { SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP } from 'cio/utils/metrics/segment-membership';
import moment from 'moment-timezone';
import EnvironmentModel from 'cio/models/environment';
import DS from 'ember-data';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { Grouping } from 'cio/utils/metrics/resolution';
import { TimeRange } from 'cio/utils/time';
import { sortByCaseInsensitive } from 'cio/utils/sort';

interface DashboardPageSegmentsCardArgs {
  environmentModel: EnvironmentModel;
  selectedSegments: (string | null)[];
  timeRange: TimeRange;
  grouping: Grouping;
  isLoading: boolean;
  isUpdating: boolean;
  onSegmentsChanged: (segmentIds: string[]) => unknown;
  onTrackNavigate: (data: Record<string, any>) => unknown;
  onTrackChartChanged: (data: Record<string, any>) => unknown;
  onTrackChartHover: (data: Record<string, any>) => unknown;
}

export default class DashboardPageSegmentsCard extends Component<DashboardPageSegmentsCardArgs> {
  @service store!: DS.Store;

  @tracked environmentId: string | null = null;

  get isLoading() {
    return this.args.isLoading || this.fetchSegments.isRunning;
  }

  segmentMembershipReleaseDate = SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP;

  get isTimeRangeStartBeforeSegmentMinDate() {
    return (this.args.timeRange?.start?.unix() ?? 0) < SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP;
  }

  get adjustedTimeRange() {
    let utcRange = {
      start: moment.tz(`${this.args.timeRange.start?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
      end: moment.tz(`${this.args.timeRange.end?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
    };

    if (utcRange.start.unix() < SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP) {
      utcRange.start = moment.unix(SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP).utc();
    }

    return utcRange;
  }

  willDestroy() {
    super.willDestroy();

    this.fetchSegments.cancelAll({ resetState: true });
  }

  @computed('fetchSegments.lastSuccessful.value')
  get segments() {
    return sortByCaseInsensitive(this.fetchSegments.lastSuccessful?.value?.toArray() ?? [], ['name']);
  }

  fetchSegments = task({ drop: true }, async () => {
    return this.store.findAll('short-segment');
  });

  @action
  onInsert() {
    this.environmentId = this.args.environmentModel.id;

    // Reset previous states, because the environment may have changed, so the
    // old segment IDs have no use anymore
    this.fetchSegments.cancelAll({ resetState: true });
    this.fetchSegments.perform();
  }

  @action
  onUpdate() {
    if (this.environmentId !== this.args.environmentModel.id) {
      this.onInsert();
    }
  }

  @action
  onSegmentSelected(index: number, segment: SegmentModel) {
    let segmentIds = this.args.selectedSegments.map((id) => id ?? '0');
    segmentIds[index] = segment.id;

    this.args.onSegmentsChanged(segmentIds);

    this.args.onTrackChartChanged({
      editType: 'segment',
      card: 'segments',
      model: segment,
    });
  }
}
